import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { FaHeart, FaMoon, FaSun, FaUser } from "react-icons/fa";
import { FiSun } from "react-icons/fi";
import { FiLogOut, FiUser } from "react-icons/fi";
import { matchPath, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUserContext } from "../../context/UserContext";
import { useFilterContext } from "../../context/FilterContext";
import { useFavoriteListContext } from "../../context/FavoriteListContext";
import lightLogo from "../../assets/images/light_logo_deloitte.png";
import { BsQuestionCircleFill } from "react-icons/bs";
import { FiDownload, FiMessageSquare, FiPlusCircle, FiSearch } from "react-icons/fi";
import { TiHome } from "react-icons/ti";
import chatIcon from "../../assets/icons/ChatIcon.svg";
import settingsIcon from "../../assets/icons/settingIcon.svg";
import { AddRegulatorModal } from "./components/AddRegulatorModal/AddRegulatorModal";
import { FeedbackModal } from "./components/FeedbackModal/FeedbackModal";
import { IFavoriteItem } from "../../context/interfaces/FavoriteItem";
import { useMutation } from "@tanstack/react-query";
import { addFavoriteItem } from "../../pages/HomePage/components/ListBlock/components/ListItem/services/addFavoriteItem.service";
import { deleteFavoriteItem } from "../../pages/HomePage/components/ListBlock/components/ListItem/services/deleteFavoriteItem.service";

interface IHeaderProps {
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
}

const Header: React.FC<IHeaderProps> = ({ darkMode, setDarkMode }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isHomePage =
    location.pathname === "/recent" || location.pathname === "/favorite";
  const isChatAi = location.pathname === "/chatAI";
  const isAdminPanel = location.pathname.startsWith("/admin_panel");
  const match = matchPath("/details/:regulator/:id", location.pathname);
  const itemRegulator = match?.params?.regulator ?? "";
  const itemId = match?.params?.id ?? "";
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { filterState } = useFilterContext();
  const [showAddRegulatorModal, setShowAddRegulatorModal] = useState(false);
  const { favoriteList, setFavoriteList } = useFavoriteListContext();
  const isFavourite = favoriteList.some(
    (favItem: IFavoriteItem) => favItem.case_no === itemId,
  );
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userBtnRef = useRef<HTMLButtonElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false);
  const helpBtnRef = useRef<HTMLButtonElement>(null);
  const helpMenuRef = useRef<HTMLDivElement>(null);

  const [avatarBroken, setAvatarBroken] = useState(false);
  const avatarUrl = (user?.avatar ?? "").trim();
  const hasAvatar = !!avatarUrl && !avatarBroken;
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const { mutate: mutateCreateFavoriteItem } = useMutation({
    mutationFn: async () => await addFavoriteItem(itemRegulator, itemId),
    onSuccess: (newFavoriteItem) => {
      setFavoriteList((prev) => [...prev, newFavoriteItem]);
    },
    onError: (error: Error) => {
      console.error("Error creating favorite item:", error);
    },
  });

  const { mutate: mutateDeleteFavoriteItem } = useMutation({
    mutationFn: async () => await deleteFavoriteItem(itemRegulator, itemId),
    onSuccess: () => {
      setFavoriteList((prev) =>
        prev.filter((favItem) => favItem.case_no !== itemId),
      );
    },
    onError: (error: Error) => {
      console.error("Error deleting favorite item:", error);
    },
  });
  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        isUserMenuOpen &&
        userMenuRef.current &&
        !userMenuRef.current.contains(t) &&
        userBtnRef.current &&
        !userBtnRef.current.contains(t)
      ) {
        setIsUserMenuOpen(false);
      }
      if (
        isHelpMenuOpen &&
        helpMenuRef.current &&
        !helpMenuRef.current.contains(t) &&
        helpBtnRef.current &&
        !helpBtnRef.current.contains(t)
      ) {
        setIsHelpMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsUserMenuOpen(false);
        setIsHelpMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [isUserMenuOpen, isHelpMenuOpen]);

  const handleLogoClick = () => {
    const query = getFilterSearchParams(filterState);
    navigate({ pathname: "/recent", search: query });
  };

  const toggleChatButton = () => {
    const newStatus = isHomePage || isAdminPanel;
    const query = getFilterSearchParams(filterState);
    navigate({
      pathname: newStatus
        ? "/chatAI"
        : localStorage.getItem("isFavorite") === "true"
          ? "/favorite"
          : "/recent",
      search: query,
    });
  };

  const handleLanguageChange = (selectedOption: {
    value: string;
    label: string;
  }) => {
    i18n.changeLanguage(selectedOption.value);
    localStorage.setItem("language", selectedOption.value);
  };

  const languageOptions = [
    { value: "en", label: "English" },
    { value: "pl", label: "Polish" },
    { value: "cs", label: "Czech" },
    { value: "ro", label: "Romanian" },
    { value: "hu", label: "Hungarian" },
  ];

  const handleLogout = () => {
    window.location.href = `${window.location.origin}/.auth/logout`;
  };

  const toggleDarkMode = (isDarkMode: boolean) => {
    setDarkMode(isDarkMode);
  };

  const getFilterSearchParams = (filters: any) => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => v && params.append(key, v));
      } else if (value instanceof Date) {
        params.set(key, value.toISOString());
      } else if (value) {
        params.set(key, value.toString());
      }
    });

    return params.toString();
  };
  const handleAddFavorite = () => {
    mutateCreateFavoriteItem();
  };

  const handleRemoveFavorite = () => {
    mutateDeleteFavoriteItem();
  };
  return (
    <header className="bg-black h-[48px] px-3 py-4 flex justify-between items-center">
      <div className="flex items-center space-x-4">
        <img
          alt="Logo"
          src={lightLogo}
          className="w-[85px] h-[16px]"
          draggable="false"
        />
        <div className="h-3 w-px bg-[#53565A]" />
        <h1
          className="text-base leading-none text-white cursor-pointer font-medium"
          onClick={handleLogoClick}
        >
          Legal Insight Monitor
        </h1>
      </div>
      <div className="flex items-center space-x-3">
        {!isChatAi && !isHomePage && !isAdminPanel && (
          <>
            <div className="flex items-center justify-end space-x-4">
              <button
                onClick={() =>
                  isFavourite ? handleRemoveFavorite() : handleAddFavorite()
                }
                className="fixed bottom-6 right-6 p-2 rounded-full bg-del-gray text-white shadow-lg group"
              >
                <FaHeart
                  className={`text-xl transition-colors duration-200
      ${isFavourite ? "text-del-green" : "text-white group-hover:text-del-green"}
    `}
                />
              </button>
            </div>
          </>
        )}
      </div>
      <div className="flex items-center space-x-4">
        {(isHomePage || isChatAi || isAdminPanel) && (
          <>
            <button
              onClick={toggleChatButton}
              className="relative text-sm font-semibold text-del-light-theme-green flex items-center"
            >
              {isHomePage || isAdminPanel ? (
                <>
                  {t("chatButton")}
                  <img
                    src={chatIcon}
                    alt="chat"
                    className="w-4 h-4 ml-2 -mr-2"
                  />
                </>
              ) : (
                <>
                  {t("searchButton")}
                  <FiSearch className="w-4 h-4 ml-2 -mr-2" />
                </>
              )}
            </button>
            <div className="h-3 w-px bg-[#53565A]" />
          </>
        )}

        <div className="relative flex items-center h-full text-white -right-0.5">
          <button
            onClick={() => {
              if (isAdminPanel) {
                const query = getFilterSearchParams(filterState);
                navigate({ pathname: "/recent", search: query });
              } else {
                navigate("/admin_panel/manage_users");
              }
            }}
            className="w-[44px] h-[40px] flex items-center justify-center hover:bg-[#555555] focus:bg-[#444444] focus:outline-none"
          >
            {isAdminPanel ? (
              <TiHome className="w-5 h-5 bold text-white" />
            ) : (
              <img src={settingsIcon} alt="chat" className="w-5 h-5" />
            )}
          </button>

          <button
            ref={helpBtnRef}
            onClick={() => setIsHelpMenuOpen((v) => !v)}
            className="w-[44px] h-[40px] flex items-center justify-center hover:bg-[#555555] focus:bg-[#444444] focus:outline-none"
            aria-haspopup="menu"
            aria-expanded={isHelpMenuOpen}
          >
            <BsQuestionCircleFill className="w-4 h-4 text-white" />
          </button>

          {isHelpMenuOpen && (
            <div
              ref={helpMenuRef}
              className="absolute right-0 top-11 z-50 w-56 text-sm font-normal rounded-xl border border-slate-700/80 bg-black/95 shadow-2xl text-white backdrop-blur"
              role="menu"
            >
              <a
                href="/files/Legal%20Insight%20Monitor%20User%20Guide.pdf"
                download
                onClick={() => setIsHelpMenuOpen(false)}
                className="w-full flex items-center gap-2 px-4 py-2 text-left rounded-lg hover:bg-del-green/90 transition-colors font-normal"
              >
                <FiDownload className="h-4 w-4" />
                <span className="help-menu-item-text">{t("downloadUserGuise")}</span>
              </a>

              <button
                onClick={() => {
                  setShowAddRegulatorModal(true);
                  setIsHelpMenuOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-left rounded-lg hover:bg-del-green/90 transition-colors font-normal"
              >
                <FiPlusCircle className="h-4 w-4" />
                <span className="help-menu-item-text">
                  {t("adminPanel.addRegulator")}
                </span>
              </button>
              <button
                onClick={() => setShowFeedbackModal(true)}
                className="w-full flex items-center gap-2 px-4 py-2 text-left rounded-lg hover:bg-del-green/90 text-white transition-colors font-normal"
              >
                <FiMessageSquare className="h-4 w-4" />
                <span className="help-menu-item-text">
                  {t("feedback.feedbackTitle")}
                </span>
              </button>
            </div>
          )}
        </div>
        <div className="relative inline-block w-auto pr-[6px]">
          <Select
            isSearchable={false}
            options={languageOptions}
            value={languageOptions.find(
              (option) => option.value === i18n.language,
            )}
            onChange={(selectedOption) => handleLanguageChange(selectedOption!)}
            classNamePrefix="react-select"
            getOptionLabel={(option) => option.value.toUpperCase()}
            getOptionValue={(option) => option.value}
            styles={{
              control: (base) => ({
                ...base,
                backgroundColor: "transparent",
                border: "none",
                boxShadow: "none",
                minHeight: "auto",
                height: "auto",
                cursor: "pointer",
                fontSize: "14px",
              }),
              valueContainer: (base) => ({
                ...base,
                padding: 0,
                margin: 0,
              }),
              singleValue: (base) => ({
                ...base,
                color: "#ffffff",
                margin: 0,
                padding: 0,
              }),
              container: (base) => ({
                ...base,
                width: "auto",
                minWidth: "25px",
                maxWidth: "30px",
              }),
              menu: (base) => ({
                ...base,
                backgroundColor: "#000000",
                top: "27px",
                right: "-3px",
                zIndex: 9999,
                minWidth: "44px",
                borderRadius: 10,
                border: "1px solid rgba(51, 65, 85, 0.8)",
                boxShadow: "0 12px 28px rgba(0,0,0,0.4)",
              }),
              option: (base, { isFocused }) => ({
                ...base,
                backgroundColor: isFocused ? "#86BC25" : "#000000",
                color: "#FFFFFF",
                padding: "6px 10px",
                fontSize: "14px",
                borderRadius: 8,
              }),
              dropdownIndicator: () => ({
                display: "none",
              }),
              indicatorSeparator: () => ({
                display: "none",
              }),
              indicatorsContainer: () => ({
                display: "none",
              }),
            }}
          />
        </div>

        <button
          ref={userBtnRef}
          className={`flex items-center rounded-full overflow-hidden ${hasAvatar ? "" : "p-[2px] bg-white"}`}
          onClick={() => setIsUserMenuOpen((v) => !v)}
        >
          {hasAvatar ? (
            <img
              src={avatarUrl}
              alt="User avatar"
              className="h-7 w-7 object-cover rounded-full"
              onError={() => setAvatarBroken(true)}
            />
          ) : (
            <div className="h-7 w-7 flex items-center justify-center bg-white rounded-full">
              <FaUser className="h-3 w-3 text-black" />
            </div>
          )}
        </button>
        {isUserMenuOpen && (
          <div
            ref={userMenuRef}
            className="absolute right-0 top-12 z-50 w-64 rounded-2xl border border-slate-700/80 bg-black/95 shadow-2xl backdrop-blur"
            role="menu"
          >
            {/* Profile */}
            <div className="px-4 py-3 border-b border-slate-700/60">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center">
                  <FiUser className="h-4 w-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {user.name
                      ? user.name.split("<")[0].trim()
                      : "Name Surname"}
                  </p>
                  <p className="text-xs text-slate-400 truncate">
                    {user.email ? user.email : "Email"}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="px-2 py-2">
              <button
                onClick={handleLogout}
                className="w-full text-sm font-normal flex items-center gap-2 px-3 py-2 text-left rounded-lg hover:bg-del-green/90 transition-colors text-white"
              >
                <FiLogOut className="h-4 w-4" />
                <span className="font-normal">{t("logOut")}</span>
              </button>
            </div>
          </div>
        )}
      </div>
      {showAddRegulatorModal && (
        <AddRegulatorModal
          setShowAddRegulatorModal={setShowAddRegulatorModal}
          darkMode={darkMode}
        />
      )}
      {showFeedbackModal && (
        <FeedbackModal
          setShowModal={setShowFeedbackModal}
          darkMode={darkMode}
          regulatorCodeParam={itemRegulator}
          idParam={itemId}
        />
      )}
    </header>
  );
};

export { Header };
